/*
 * The reply box's files (docs/2026-10-19-inbox-reply-attachments-implementation-plan.md, section 4).
 * The server holds them (`reply-attachment-routes.ts`); this browser keeps only the box's id beside
 * the unsent text (`unsent-reply.ts`). Uploads go one at a time, each against the revision the box was
 * last seen at, so a removal and an upload never cross; a retry of an upload is the same upload. A
 * file that did not go up stays in the list with what to do about it until the rep retries or removes
 * it: it is never left out silently and the rest sent. An answer that comes back after the rep moved
 * on lands on its own conversation's box, never on the one now open.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState, useSyncExternalStore } from "react";
import type {
  ReplyFile,
  ReplyFileSelection,
  ReplyFilesStatus,
} from "../../src/inbox-v2/contract.ts";
import { replyFileName } from "../../src/reply-attachment-policy.ts";
import { InboxApiError, inboxApi } from "./api.ts";
import { keepComposer, readComposer } from "./unsent-reply.ts";

/** A file on its way up, or one that did not make it. */
export type PendingFile = {
  readonly uploadId: string;
  readonly name: string;
  readonly bytes: number;
  readonly state: "uploading" | "failed";
  /** What went wrong and what to do, in the rep's words; null while uploading. */
  readonly problem: string | null;
  /** Whether trying the same upload again can work. */
  readonly retryable: boolean;
};

/** Why an upload was refused, said with what to do. */
const PROBLEMS: Readonly<Record<string, string>> = {
  file_too_large: "Over Bison's 10 MB for one file. Choose a smaller file.",
  total_too_large: "Over Bison's 25 MB for one reply. Remove a file to make room.",
  file_empty: "This file is empty.",
  file_name_invalid: "This file's name cannot be sent. Rename it, then attach it again.",
  file_type_unsupported:
    "Bison does not send this kind of file. Documents, images, text, archives, audio and video go.",
  duplicate_file: "This file is already attached.",
  stale_turn: "The lead wrote again. Read the new message, then attach the file again.",
  storage_unavailable: "Files cannot be stored right now. Try again in a moment.",
  files_unavailable: "Files cannot go on replies here right now.",
  uploads_busy: "Many files are uploading. Try again in a moment.",
  folder: "Folders cannot be attached. Attach the files inside it.",
};
/** A draft's files that did not come into the box with its words: never dropped unsaid. */
const DRAFT_FILES = "Files from the draft";
const DRAFT_FILES_PROBLEM =
  "The draft's files did not load into this reply. Try again, or remove this to send without them.";
const INTERRUPTED = "The upload was interrupted. Try again.";
/** Refusals that the same bytes will meet again: no Retry is offered. */
const FINAL = new Set([
  "file_too_large",
  "total_too_large",
  "file_empty",
  "file_name_invalid",
  "file_type_unsupported",
  "duplicate_file",
]);

const EMPTY: ReplyFile[] = [];

/** A file that did not go up, with what to do; one the same bytes would meet again offers no retry. */
function failedFile(
  entry: { uploadId: string; name: string; bytes: number },
  code: string | null,
): PendingFile {
  return {
    ...entry,
    state: "failed",
    problem: (code && PROBLEMS[code]) || INTERRUPTED,
    retryable: !code || !FINAL.has(code),
  };
}

/** Why the server would refuse this file now, checked here first; null when it may go up. */
function localRefusal(
  file: File,
  total: number,
  limits: ReplyFilesStatus["limits"],
): string | null {
  if (file.size === 0) return "file_empty";
  if (file.size > limits.fileBytes) return "file_too_large";
  if (total + file.size > limits.combinedBytes) return "total_too_large";
  return replyFileName(file.name) === null ? "file_name_invalid" : null;
}

/**
 * The list entries for files chosen and folders refused, and which files start uploading: each
 * counted against the reply's room as the ones before it would fill it.
 */
function planAdditions(
  chosen: readonly File[],
  refused: readonly string[],
  limits: ReplyFilesStatus["limits"],
  used: number,
) {
  let total = used;
  const additions: PendingFile[] = refused.map((name) =>
    failedFile({ uploadId: crypto.randomUUID(), name, bytes: 0 }, "folder"),
  );
  const starts: { file: File; uploadId: string }[] = [];
  for (const file of chosen) {
    const entry = { uploadId: crypto.randomUUID(), name: file.name, bytes: file.size };
    const reason = localRefusal(file, total, limits);
    if (reason) {
      additions.push(failedFile(entry, reason));
      continue;
    }
    total += file.size;
    additions.push({ ...entry, state: "uploading", problem: null, retryable: false });
    starts.push({ file, uploadId: entry.uploadId });
  }
  return { additions, starts };
}

/** One change against the box's last revision; if the box moved under this tab, read it and try once more. */
async function againstRevision(
  revision: number,
  attempt: (revision: number) => Promise<ReplyFileSelection>,
  reread: () => Promise<ReplyFilesStatus>,
): Promise<ReplyFileSelection> {
  try {
    return await attempt(revision);
  } catch (error) {
    if (!(error instanceof InboxApiError) || error.code !== "stale_selection") throw error;
    return attempt((await reread()).selection.revision);
  }
}

const withoutUpload = (uploadId: string) => (files: readonly PendingFile[]) =>
  files.filter((entry) => entry.uploadId !== uploadId);

/**
 * Uploads and refusals per box, held outside any one box's component: a rep who leaves a conversation
 * while its file goes up and comes back finds the file still going up, or why it did not, never an
 * empty box whose Send would go without it. A late answer lands on its own box, whatever is open.
 */
const uploads = (() => {
  let byBox: Readonly<Record<string, readonly PendingFile[]>> = {};
  const listeners = new Set<() => void>();
  return {
    read: () => byBox,
    change(box: string, change: (files: readonly PendingFile[]) => readonly PendingFile[]) {
      byBox = { ...byBox, [box]: change(byBox[box] ?? []) };
      for (const listener of listeners) listener();
    },
    subscribe(listener: () => void) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
    /** One upload at a time per box, each against the revision the one before it left. */
    chains: new Map<string, Promise<void>>(),
    aborts: new Map<string, AbortController>(),
    /** The File of each upload, for Retry; out of state, which a render compares. */
    sources: new Map<string, File>(),
    /** The draft revision whose files a failed load was for, for Retry. */
    drafts: new Map<string, number>(),
  };
})();

/** A draft's files that did not load into the box: one entry holding Send, with Try again. */
function draftFilesMissed(box: string, draftRevision: number): void {
  const uploadId = crypto.randomUUID();
  uploads.drafts.set(uploadId, draftRevision);
  const entry = failedFile({ uploadId, name: DRAFT_FILES, bytes: 0 }, null);
  uploads.change(box, (files) => [...files, { ...entry, problem: DRAFT_FILES_PROBLEM }]);
}

/** The box as a send or a draft save names it, or undefined with no files. */
function boxReference(
  status: ReplyFilesStatus | undefined,
): { composerId: string; revision: number; fileIds: string[] } | undefined {
  const selection = status?.selection;
  if (!selection || selection.files.length === 0) return undefined;
  const { composerId, revision, files } = selection;
  return { composerId, revision, fileIds: files.map((file) => file.id) };
}

/** The box for this key: the kept one, or a new one, kept once a file starts going up to it. */
function composerFor(key: string) {
  const kept = readComposer(key);
  return { key, id: kept ?? crypto.randomUUID(), kept: kept !== null };
}

/** The box's id for this conversation, and whether this browser kept it (it may hold files). */
function useComposerId(key: string): [{ id: string; kept: boolean }, () => void] {
  const [state, setState] = useState(() => composerFor(key));
  const current = state.key === key ? state : composerFor(key);
  if (current !== state) setState(current);
  const renew = useCallback(() => setState({ key, id: crypto.randomUUID(), kept: false }), [key]);
  return [current, renew];
}

/** Taking a file off: which one is going, and which one would not go, said at that file. */
function useRemoval(change: (fileId: string) => Promise<void>) {
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeFailed, setRemoveFailed] = useState<string | null>(null);
  const remove = async (fileId: string) => {
    setRemoving(fileId);
    setRemoveFailed(null);
    try {
      await change(fileId);
    } catch {
      setRemoveFailed(fileId);
    } finally {
      setRemoving(null);
    }
  };
  return { removing, removeFailed, remove: (fileId: string) => void remove(fileId) };
}

interface ReplyFilesInput {
  workspaceId: string;
  planId: string;
  /** The lead message the reply answers; files go only on a reply to the newest. */
  messageId: string | null;
  /** The unsent text's key, under which the box's id is kept too. */
  unsentKey: string;
  /** The reply is editable and its send status is ready; otherwise no files are read or offered. */
  enabled: boolean;
  mode?: "reply" | "forward";
}

export function useReplyFiles(input: ReplyFilesInput) {
  const { workspaceId, planId, messageId, unsentKey } = input;
  const client = useQueryClient();
  const [composer, renew] = useComposerId(unsentKey);
  const composerId = composer.id;
  const queryKey = ["inbox-v2", workspaceId, "reply-files", planId, composerId] as const;
  const status = useQuery({
    queryKey,
    queryFn: ({ signal }) => inboxApi.replyFiles(workspaceId, planId, composerId, signal),
    enabled: input.enabled,
    refetchOnMount: "always",
  });
  const pendingBy = useSyncExternalStore(uploads.subscribe, uploads.read);
  const box = `${planId}\u0000${composerId}`;
  const pending = pendingBy[box] ?? [];
  const setPending = uploads.change;
  const revisionOf = (key: typeof queryKey) =>
    client.getQueryData<ReplyFilesStatus>(key)?.selection.revision ?? 0;
  const reread = async (key: typeof queryKey) => {
    const fresh = await inboxApi.replyFiles(workspaceId, planId, key[4]);
    client.setQueryData(key, fresh);
    return fresh;
  };
  /** The box as the server answered a change; a read of it already under way is older, so dropped. */
  const store = async (key: typeof queryKey, selection: ReplyFileSelection) => {
    await client.cancelQueries({ queryKey: key, exact: true });
    client.setQueryData<ReplyFilesStatus>(key, (old) => (old ? { ...old, selection } : old));
  };

  const upload = (key: typeof queryKey, owner: string, file: File, uploadId: string) => {
    const id = key[4];
    const next = (uploads.chains.get(owner) ?? Promise.resolve()).then(async () => {
      const controller = new AbortController();
      uploads.aborts.set(uploadId, controller);
      const attempt = async (revision: number) =>
        inboxApi.uploadReplyFile(workspaceId, planId, {
          composerId: id,
          uploadId,
          revision,
          messageId: messageId ?? "",
          ...(input.mode === "forward" ? { forward: true } : {}),
          file,
          signal: controller.signal,
        });
      try {
        const next = await againstRevision(revisionOf(key), attempt, () => reread(key));
        await store(key, next);
        uploads.sources.delete(uploadId);
        setPending(owner, withoutUpload(uploadId));
      } catch (error) {
        if (controller.signal.aborted) {
          setPending(owner, withoutUpload(uploadId));
          void client.invalidateQueries({ queryKey: key, exact: true });
          return;
        }
        const code = error instanceof InboxApiError ? error.code : null;
        setPending(owner, (files) =>
          files.map((entry) => (entry.uploadId === uploadId ? failedFile(entry, code) : entry)),
        );
      } finally {
        uploads.aborts.delete(uploadId);
      }
    });
    uploads.chains.set(owner, next);
  };

  /** Adds files: each is checked here as the server would, then sent up in turn. */
  const add = (chosen: readonly File[], refused: readonly string[] = []) => {
    const known = client.getQueryData<ReplyFilesStatus>(queryKey);
    if (!known?.available || !messageId) return;
    const used =
      known.selection.files.reduce((sum, file) => sum + file.bytes, 0) +
      pending.reduce((sum, file) => sum + (file.state === "uploading" ? file.bytes : 0), 0);
    const owner = box;
    const { additions, starts } = planAdditions(chosen, refused, known.limits, used);
    // Kept as the first file starts going up, not when it lands: a rep who leaves and returns
    // meanwhile comes back to this box, and to the upload still under way in it.
    if (starts.length > 0) keepComposer(unsentKey, composerId);
    setPending(owner, (files) => [...files, ...additions]);
    for (const start of starts) {
      uploads.sources.set(start.uploadId, start.file);
      upload(queryKey, owner, start.file, start.uploadId);
    }
  };

  const retry = (uploadId: string) => {
    const draftRevision = uploads.drafts.get(uploadId);
    if (draftRevision !== undefined) {
      dismiss(uploadId);
      void adopt(draftRevision);
      return;
    }
    const file = uploads.sources.get(uploadId);
    if (!file) return;
    setPending(box, (files) =>
      files.map((entry) =>
        entry.uploadId === uploadId ? { ...entry, state: "uploading", problem: null } : entry,
      ),
    );
    upload(queryKey, box, file, uploadId);
  };

  /** Takes a file off: one still uploading is stopped, one that failed is let go. */
  const dismiss = (uploadId: string) => {
    uploads.aborts.get(uploadId)?.abort();
    uploads.sources.delete(uploadId);
    uploads.drafts.delete(uploadId);
    setPending(box, withoutUpload(uploadId));
  };

  // The files the box last took from a draft, to tell a rep's change of them from the draft's own.
  const [adoptedBy, setAdoptedBy] = useState<Readonly<Record<string, readonly string[]>>>({});

  const removal = useRemoval(async (fileId) => {
    const key = queryKey;
    const attempt = async (revision: number) =>
      inboxApi.removeReplyFile(workspaceId, planId, key[4], fileId, revision);
    await store(key, await againstRevision(revisionOf(key), attempt, () => reread(key)));
  });

  /**
   * The box takes the agent draft's files, as "Edit in reply box" loads its words. Files that do not
   * come (files off here, the sender updating, a store down) stay in the list as one entry that holds
   * Send, with Try again and Remove: the words never arrive alone without the box saying so.
   */
  const adopt = async (draftRevision: number) => {
    if (!messageId || !input.enabled) return;
    const key = queryKey;
    const owner = box;
    const attempt = async (revision: number) =>
      inboxApi.adoptDraftFiles(workspaceId, planId, {
        composerId: key[4],
        expectedRevision: revision,
        messageId,
        draftRevision,
      });
    try {
      const next = await againstRevision(revisionOf(key), attempt, () => reread(key));
      if (next.files.length > 0) keepComposer(unsentKey, key[4]);
      await store(key, next);
      setAdoptedBy((all) => ({ ...all, [owner]: next.files.map((file) => file.id) }));
    } catch {
      draftFilesMissed(owner, draftRevision);
    }
  };

  /**
   * After a send went: the server emptied the box if it still held exactly what went. An emptied box
   * is forgotten here and the next reply starts a new one; one the rep changed meanwhile is kept.
   */
  const settle = async () => {
    const key = queryKey;
    const fresh = await client.fetchQuery({
      queryKey: key,
      queryFn: () => inboxApi.replyFiles(workspaceId, planId, key[4]),
    });
    if (fresh.selection.files.length > 0) return;
    keepComposer(unsentKey, null);
    renew();
  };

  const files = status.data?.selection.files ?? EMPTY;
  const adopted = adoptedBy[box];
  const ids = files.map((file) => file.id);
  // Without a record of what a draft brought (a reload), files in the box count as the rep's.
  const edited = adopted ? adopted.join() !== ids.join() : ids.length > 0;
  return {
    composerId,
    available: status.data?.available === true,
    reason: status.data?.reason ?? null,
    limits: status.data?.limits ?? null,
    files,
    pending,
    uploading: pending.some((file) => file.state === "uploading"),
    /**
     * Nothing going up or refused, and a kept box read: what is listed is what would go. A box never
     * kept holds no files, so its reply goes even where files cannot be read (an older server's 404).
     */
    ready: pending.length === 0 && (!composer.kept || status.data !== undefined),
    ...removal,
    add,
    retry,
    dismiss,
    adopt,
    settle,
    /** The box's files differ from those its draft brought in. */
    edited,
    /** The box as a send or a draft save names it, or undefined with no files. */
    reference: () => boxReference(status.data),
    href: (fileId: string) => inboxApi.replyFileHref(workspaceId, planId, fileId),
  };
}

export type ReplyFiles = ReturnType<typeof useReplyFiles>;
